import React, { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  Image as ImageIcon,
  Loader2,
  RotateCcw,
  Video,
  X,
} from 'lucide-react';
import { GeneratedImage } from '../../services/api/imageGenerationService';
import { BannerVideoEntry } from '../../services/api/bannerVideoService';
import videoGenerationService from '../../services/api/videoGenerationService';
import useBannerVideo, {
  isBannerVideoActive,
} from '../../hooks/useBannerVideo';
import { track, ANALYTICS_EVENTS } from '../../utils/productAnalytics';
import './BannerVideoWorkspace.scss';

interface ImagePanelActions {
  onConvertToVideo: () => void;
  videoActionLabel: string;
  videoActionDisabled: boolean;
}

interface Props {
  image: GeneratedImage;
  title: string;
  onClose: () => void;
  imageControls?: React.ReactNode;
  renderImagePanel: (actions: ImagePanelActions) => React.ReactNode;
}

const progressLabel = (entry?: BannerVideoEntry): string => {
  if (!entry) return '生成の準備をしています';
  if (entry.job.status === 'pending') return '順番を待っています';
  const labels: Record<string, string> = {
    initializing: '元のバナーを読み込んでいます',
    analyzing_source_video: '元動画の動きと構成を確認しています',
    optimizing_prompt: '動画の指示を準備しています',
    generating: 'バナーに動きをつけています',
    downloading: '生成した動画を取得しています',
    resizing: '元のバナーと同じサイズに仕上げています',
    fitting_banner: '元のバナーと同じサイズに仕上げています',
    retrying: '接続を確認して、生成の続きを取得しています',
    uploading: '動画を保存しています',
    saving: '動画を保存しています',
    completed: '動画を読み込んでいます',
  };
  return labels[entry.job.progress_step || ''] || 'バナーに動きをつけています';
};

const Progress: React.FC<{ entry?: BannerVideoEntry }> = ({ entry }) => {
  const progress = Math.max(
    0,
    Math.min(99, entry?.job.progress_percentage || 0)
  );
  return (
    <div
      className="banner-video-progress"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="banner-video-progress__icon">
        <Loader2 size={24} aria-hidden="true" />
      </span>
      <p className="banner-video-progress__title">動画を生成中</p>
      <p>{progressLabel(entry)}</p>
      <div
        role="progressbar"
        aria-label="動画の生成進捗"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
        className="banner-video-progress__bar"
      >
        <span style={{ width: `${progress}%` }} />
      </div>
      <span className="banner-video-progress__percentage">{progress}%</span>
      <p className="banner-video-progress__hint">
        この画面を閉じても生成は続きます
      </p>
    </div>
  );
};

/** The preview stays mounted at one size while image/video actions change alongside it. */
const BannerVideoWorkspace: React.FC<Props> = ({
  image,
  title,
  onClose,
  imageControls,
  renderImagePanel,
}) => {
  const state = useBannerVideo(image.id);
  const [instructions, setInstructions] = useState<Record<string, string>>({});
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [mediaErrorId, setMediaErrorId] = useState<string | null>(null);
  const [mediaReloadKey, setMediaReloadKey] = useState(0);
  const navRef = useRef<HTMLDivElement>(null);
  const panelHeadingRef = useRef<HTMLHeadingElement>(null);
  const showStarting = state.selectedId === 'starting';
  const selected = state.entries.find(
    (entry) => entry.job.id === state.selectedId
  );
  const isImage = state.selectedId === 'image' || (!selected && !showStarting);
  const hasStartingSlide = showStarting || state.isSubmitting || !!state.error;
  const slideIds = [
    'image',
    ...state.entries.map((entry) => entry.job.id),
    ...(hasStartingSlide ? ['starting'] : []),
  ];
  const slideIndex = Math.max(0, slideIds.indexOf(state.selectedId));
  const version = selected
    ? state.entries.indexOf(selected) + 1
    : state.entries.length + 1;
  const instruction = selected
    ? (instructions[selected.job.id] ?? selected.job.instruction ?? '')
    : '';
  const busy = state.isSubmitting || !!state.activeEntry;
  const failed = selected?.job.status === 'failed';
  const missingVideo = selected?.job.status === 'completed' && !selected.video;
  const size =
    selected?.job.source_width && selected.job.source_height
      ? { width: selected.job.source_width, height: selected.job.source_height }
      : naturalSize;

  useEffect(() => {
    const selectedButton = navRef.current?.querySelector<HTMLElement>(
      '[aria-pressed="true"]'
    );
    // Scroll the strip only; scrollIntoView would also move the surrounding modal.
    if (selectedButton && navRef.current) {
      navRef.current.scrollLeft =
        selectedButton.offsetLeft - navRef.current.offsetLeft - 12;
    }
    setDownloadError(null);
    if (document.activeElement === document.body)
      panelHeadingRef.current?.focus();
  }, [state.selectedId]);

  const download = async () => {
    if (!selected?.video || isDownloading) return;
    setIsDownloading(true);
    setDownloadError(null);
    try {
      // Refresh the signed URL before saving, including after a long generation wait.
      const detail = await videoGenerationService.getJobDetail(selected.job.id);
      if (!detail.video?.url) throw new Error('動画が見つかりません。');
      const response = await fetch(detail.video.url);
      if (!response.ok) throw new Error('動画を取得できませんでした。');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `banner-${image.id}-video-${version}.mp4`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      track(ANALYTICS_EVENTS.DOWNLOAD_COMPLETED, { type: 'movie' });
    } catch {
      setDownloadError(
        'ダウンロードできませんでした。もう一度お試しください。'
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const retryFailed = () => {
    if (!selected) return;
    void state.create(
      selected.job.source_video_id || undefined,
      selected.job.instruction || undefined
    );
  };

  return (
    <>
      <div className="banner-video-workspace">
        <div className="banner-video-workspace__topline">
          <span>{isImage ? '元のバナー' : `動画 ${version}`}</span>
          <span>
            {size ? `${size.width} × ${size.height}` : '元のバナーと同じサイズ'}
          </span>
        </div>
        <div
          className="banner-video-workspace__viewport"
          aria-label="バナーと動画のプレビュー"
          aria-roledescription="カルーセル"
        >
          <div
            className="banner-video-workspace__track"
            style={{ transform: `translateX(-${slideIndex * 100}%)` }}
          >
            <div
              className="banner-video-workspace__slide"
              aria-hidden={!isImage}
            >
              <img
                src={image.url}
                alt={title}
                onLoad={(event) =>
                  setNaturalSize({
                    width: event.currentTarget.naturalWidth,
                    height: event.currentTarget.naturalHeight,
                  })
                }
              />
              {isImage && imageControls}
            </div>
            {state.entries.map((entry) => {
              const active = state.selectedId === entry.job.id;
              const generating = isBannerVideoActive(entry);
              return (
                <div
                  key={entry.job.id}
                  className="banner-video-workspace__slide"
                  aria-hidden={!active}
                  aria-busy={generating}
                >
                  {entry.video && active ? (
                    <>
                      <video
                        key={`${entry.video.id}-${mediaReloadKey}`}
                        src={entry.video.url}
                        poster={image.url}
                        controls
                        playsInline
                        preload="metadata"
                        aria-label={`動画 ${state.entries.indexOf(entry) + 1}のプレビュー`}
                        onError={() => setMediaErrorId(entry.job.id)}
                        onLoadedData={() => setMediaErrorId(null)}
                      />
                      {mediaErrorId === entry.job.id && (
                        <div
                          className="banner-video-workspace__media-error"
                          role="alert"
                        >
                          動画を読み込めませんでした。
                          <button
                            type="button"
                            onClick={() => {
                              setMediaErrorId(null);
                              setMediaReloadKey((key) => key + 1);
                              state.refresh();
                            }}
                          >
                            再読み込み
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <img
                        src={image.url}
                        alt=""
                        className={
                          generating || entry.job.status === 'failed'
                            ? 'banner-video-workspace__poster'
                            : ''
                        }
                      />
                      {active && generating && <Progress entry={entry} />}
                      {active && entry.job.status === 'failed' && (
                        <div className="banner-video-progress">
                          <AlertCircle size={28} aria-hidden="true" />
                          <p className="banner-video-progress__title">
                            動画を生成できませんでした
                          </p>
                          <p>右側のメッセージを確認して再試行できます。</p>
                        </div>
                      )}
                      {active &&
                        entry.job.status === 'completed' &&
                        !entry.video && (
                          <div className="banner-video-progress">
                            <AlertCircle size={28} aria-hidden="true" />
                            <p className="banner-video-progress__title">
                              動画が見つかりません
                            </p>
                            <p>元のバナーから、新しい動画を作成できます。</p>
                          </div>
                        )}
                    </>
                  )}
                </div>
              );
            })}
            {hasStartingSlide && (
              <div
                className="banner-video-workspace__slide"
                aria-hidden={!showStarting}
                aria-busy={state.isSubmitting}
              >
                <img
                  src={image.url}
                  alt=""
                  className="banner-video-workspace__poster"
                />
                {state.error ? (
                  <div className="banner-video-progress">
                    <AlertCircle size={28} aria-hidden="true" />
                    <p className="banner-video-progress__title">
                      生成の受付を確認してください
                    </p>
                    <p>右側からもう一度確認できます。</p>
                  </div>
                ) : (
                  <Progress />
                )}
              </div>
            )}
          </div>
        </div>
        <div
          className="banner-video-workspace__navigation"
          aria-label="表示する画像・動画を選択"
        >
          <button
            type="button"
            className="banner-video-workspace__arrow"
            aria-label="前のプレビュー"
            disabled={slideIndex === 0}
            onClick={() => state.setSelectedId(slideIds[slideIndex - 1])}
          >
            <ChevronLeft size={20} aria-hidden="true" />
          </button>
          <div className="banner-video-workspace__tabs" ref={navRef}>
            <button
              type="button"
              aria-pressed={isImage}
              onClick={() => state.setSelectedId('image')}
            >
              <ImageIcon size={16} aria-hidden="true" />
              元のバナー
            </button>
            {state.entries.map((entry, index) => (
              <button
                type="button"
                key={entry.job.id}
                aria-pressed={state.selectedId === entry.job.id}
                onClick={() => state.setSelectedId(entry.job.id)}
              >
                {isBannerVideoActive(entry) ? (
                  <Loader2
                    size={16}
                    className="banner-video-workspace__spinner"
                    aria-hidden="true"
                  />
                ) : entry.job.status === 'failed' ? (
                  <AlertCircle size={16} aria-hidden="true" />
                ) : (
                  <Video size={16} aria-hidden="true" />
                )}
                動画 {index + 1}
                {isBannerVideoActive(entry)
                  ? ' · 生成中'
                  : entry.job.status === 'failed'
                    ? ' · 失敗'
                    : ''}
              </button>
            ))}
            {hasStartingSlide && (
              <button
                type="button"
                aria-pressed={showStarting}
                onClick={() => state.setSelectedId('starting')}
              >
                <Loader2 size={16} aria-hidden="true" />
                {state.error ? '受付の確認' : '生成の準備中'}
              </button>
            )}
          </div>
          <button
            type="button"
            className="banner-video-workspace__arrow"
            aria-label="次のプレビュー"
            disabled={slideIndex >= slideIds.length - 1}
            onClick={() => state.setSelectedId(slideIds[slideIndex + 1])}
          >
            <ChevronRight size={20} aria-hidden="true" />
          </button>
        </div>
        {state.syncError && (
          <p className="banner-video-workspace__sync-error" role="status">
            {state.syncError}
            <button type="button" onClick={state.refresh}>
              再確認
            </button>
          </p>
        )}
      </div>

      {isImage ? (
        renderImagePanel({
          onConvertToVideo: state.openVideo,
          videoActionLabel: state.isLoading
            ? '動画を確認中'
            : busy
              ? '生成中の動画を見る'
              : state.entries.length
                ? '動画を見る'
                : '動画化する',
          videoActionDisabled: state.isLoading,
        })
      ) : (
        <aside className="banner-video-panel" aria-label="動画の操作">
          <div className="banner-video-panel__header">
            <div>
              <h3 ref={panelHeadingRef} tabIndex={-1}>
                動画バナー
              </h3>
              <p>動画 {version}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="banner-video-panel__close"
              aria-label="動画詳細を閉じる"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>
          <div className="banner-video-panel__body">
            {selected?.video ? (
              <div className="banner-video-panel__export">
                <p className="banner-video-panel__complete">
                  <Check size={16} aria-hidden="true" />
                  生成が完了しました
                </p>
                <button
                  type="button"
                  className="banner-video-panel__primary"
                  disabled={isDownloading}
                  onClick={download}
                >
                  {isDownloading ? (
                    <Loader2
                      size={18}
                      className="banner-video-workspace__spinner"
                      aria-hidden="true"
                    />
                  ) : (
                    <Download size={18} aria-hidden="true" />
                  )}
                  {isDownloading ? 'ダウンロード準備中' : '動画をダウンロード'}
                </button>
                <p>
                  MP4 · {selected.video.duration_seconds}秒 ·{' '}
                  {(selected.video.file_size / 1024 / 1024).toFixed(1)} MB
                </p>
                {selected.job.output_width &&
                  selected.job.output_height &&
                  (selected.job.output_width !== size?.width ||
                    selected.job.output_height !== size?.height) && (
                    <p>
                      保存サイズ {selected.job.output_width} ×{' '}
                      {selected.job.output_height}（元の表示比率を維持）
                    </p>
                  )}
                {downloadError && (
                  <p className="banner-video-panel__error" role="alert">
                    {downloadError}
                  </p>
                )}
              </div>
            ) : missingVideo ? (
              <div className="banner-video-panel__notice">
                <div>
                  <strong>動画を表示できません</strong>
                  <p>
                    動画が削除された可能性があります。元のバナーから作り直せます。
                  </p>
                  <button
                    type="button"
                    className="banner-video-panel__secondary"
                    disabled={busy}
                    onClick={() => void state.create()}
                  >
                    <RotateCcw size={16} aria-hidden="true" />
                    もう一度動画化する
                  </button>
                </div>
              </div>
            ) : failed || (showStarting && state.error) ? (
              <div className="banner-video-panel__error-box" role="alert">
                <p>
                  {failed
                    ? selected?.job.error_message ||
                      '動画の生成に失敗しました。もう一度お試しください。'
                    : state.error}
                </p>
                <button
                  type="button"
                  className="banner-video-panel__secondary"
                  disabled={busy}
                  onClick={failed ? retryFailed : state.retrySubmission}
                >
                  <RotateCcw size={16} aria-hidden="true" />
                  {failed ? '生成を再試行' : '生成の受付を再確認'}
                </button>
              </div>
            ) : (
              <div className="banner-video-panel__notice">
                <Loader2
                  size={18}
                  className="banner-video-workspace__spinner"
                  aria-hidden="true"
                />
                <div>
                  <strong>動画を生成中です</strong>
                </div>
              </div>
            )}

            <dl className="banner-video-panel__metadata">
              <div>
                <dt>サイズ</dt>
                <dd>
                  {size ? `${size.width} × ${size.height}` : '元のバナーと同じ'}
                </dd>
              </div>
              <div>
                <dt>長さ</dt>
                <dd>{selected?.job.duration_seconds || 8}秒</dd>
              </div>
              <div>
                <dt>生成モデル</dt>
                <dd>
                  H3 Max Turbo <span>自動</span>
                </dd>
              </div>
            </dl>

            {selected?.video && (
              <form
                className="banner-video-panel__regenerate"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (instruction.trim() && !busy)
                    void state.create(selected.video!.id, instruction.trim());
                }}
              >
                <h4>動画を再生成</h4>
                <div className="banner-video-panel__references">
                  <img src={image.url} alt="参考にする元のバナー" />
                  <span>元のバナー</span>
                  <span>＋</span>
                  <Video size={16} aria-hidden="true" />
                  <span>動画 {version}</span>
                </div>
                <label htmlFor={`banner-video-instruction-${image.id}`}>
                  変更したい内容
                </label>
                <textarea
                  id={`banner-video-instruction-${image.id}`}
                  value={instruction}
                  maxLength={4000}
                  rows={4}
                  onChange={(event) =>
                    setInstructions((current) => ({
                      ...current,
                      [selected.job.id]: event.target.value,
                    }))
                  }
                  placeholder="例：文字はそのままに、商品の動きをゆっくりにしてほしい"
                  aria-describedby={`banner-video-hint-${image.id}`}
                />
                <p
                  id={`banner-video-hint-${image.id}`}
                  className="banner-video-panel__hint"
                >
                  元の動画を残して、新しい動画を作成します。
                </p>
                <button
                  type="submit"
                  className="banner-video-panel__primary"
                  disabled={busy || !instruction.trim()}
                >
                  <RotateCcw size={17} aria-hidden="true" />
                  {busy ? '別の動画を生成中' : 'この内容で再生成'}
                </button>
              </form>
            )}

            <button
              type="button"
              className="banner-video-panel__back"
              onClick={() => state.setSelectedId('image')}
            >
              <ArrowLeft size={16} aria-hidden="true" />
              元のバナーに戻る
            </button>
          </div>
        </aside>
      )}
    </>
  );
};

export default BannerVideoWorkspace;
